// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import LazyImage from "@bg/MobiusOverrides/LazyImage/LazyImage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import { HasCategoriesState } from "@insite/client-framework/Store/Data/Categories/CategoriesState";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

function getRandomIndex(min: number, max: number) {
    // random number between images length and 0
    if (max === 1) {
        return 0;
    }

    return Math.floor(Math.random() * (max - min) + min);
}

const enum fields {
    images = "images",
}

interface ImageTileProps {
    fields: {
        largeImagePath: string;
        mediumImagePath: string;
        smallImagePath: string;
        altText: string;
        imageLink: LinkFieldValue;
        openInNewTab: boolean;
        startDate: Date;
        endDate: Date;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.images]: ImageTileProps[];
    };
}

export interface ImageStyles {
    wrapper?: InjectableCss;
    imageLink?: LinkPresentationProps;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        state,
        pages: state.pages,
        categories: state.data.categories,
    };
};

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            img {
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
};

const styles = imageStyles;

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const Advertisement = ({ fields, state, pages, categories }: Props) => {
    const [selectedImage, setSelectedImage] = React.useState({} as ImageTileProps);
    const [selectedImageURL, setSelectedImageURL] = React.useState("");
    React.useEffect(() => {
        const current = new Date().getTime();
        const images = fields.images.filter(
            image =>
                new Date(image.fields.startDate).getTime() <= current &&
                new Date(image.fields.endDate).getTime() > current,
        );
        const randomImage = images[getRandomIndex(0, images.length)];
        if (randomImage) {
            setSelectedImage(randomImage);
        }
    }, [fields]);

    React.useEffect(() => {
        let url: string | undefined = "";
        if (selectedImage && selectedImage.fields) {
            if (selectedImage.fields.imageLink.type === "Url") {
                url = selectedImage.fields.imageLink.value;
            }
            if (selectedImage.fields.imageLink.type === "Page") {
                url = getPageLinkByNodeId(state, selectedImage.fields.imageLink.value)?.url;
            }
            if (selectedImage.fields.imageLink.type === "Category") {
                const category =
                    selectedImage.fields.imageLink.type === "Category"
                        ? getCategoryState(state, selectedImage.fields.imageLink.value)?.value
                        : null;
                url = category?.path;
            }
        }

        if (url) {
            setSelectedImageURL(url);
        }
    }, [selectedImage, categories, pages]);

    if (!selectedImage || !Object.keys(selectedImage)?.length) {
        return null;
    }

    const image = (
        <LazyImage
            src={selectedImage?.fields?.largeImagePath}
            altText={selectedImage?.fields?.altText}
            disableLazyLoad={true}
        />
    );

    return (
        <>
            {selectedImageURL ? (
                <Link
                    href={selectedImageURL}
                    target={selectedImage?.fields?.openInNewTab ? "_blank" : "_self"}
                    {...styles.imageLink}
                >
                    {image}
                </Link>
            ) : (
                image
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Advertisement),
    definition: {
        displayName: "Ad. Banner",
        group: "XNGAGE" as any,
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.images,
                editorTemplate: "ListField",
                defaultValue: [],
                fieldType: "General",
                getDisplay: (item: HasFields) => {
                    return item.fields.altText;
                },
                fieldDefinitions: [
                    {
                        name: "largeImagePath",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },

                    {
                        name: "mediumImagePath",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "smallImagePath",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "altText",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "imageLink",
                        editorTemplate: "LinkField",
                        defaultValue: { type: "Page", value: "" },
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "startDate",
                        editorTemplate: "DateTimeField",
                        defaultValue: null,
                    },
                    {
                        name: "endDate",
                        editorTemplate: "DateTimeField",
                        defaultValue: null,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
